import {
  dirhams,
  type OperationSource,
  type OperationType,
  SOURCE_LABELS,
  TYPE_LABELS,
} from '@horof/shared';

/**
 * arqam's change history (its doc 54): `changes` is `{ column: [before,
 * after] }` with SQL column names and ids. Here, each line in Arabic, ids
 * as names. Numbers are only shown, never added up.
 */

const FIELD_LABELS: Record<string, string> = {
  date: 'التاريخ',
  type: 'النوع',
  amount: 'المبلغ',
  estimated_value: 'القيمة التقديرية',
  description: 'البيان',
  account_id: 'الحساب',
  from_account_id: 'من حساب',
  to_account_id: 'إلى حساب',
  project_id: 'المشروع',
  category_id: 'الصنف',
  counterparty_id: 'الجهة',
  debt_id: 'القرض',
  receipt_photo_id: 'الوصل',
  document_photo_id: 'الوثيقة',
  valuation_basis: 'أساس التقدير',
  source: 'المصدر',
  needs_review: 'تحتاج مراجعة',
  review_note: 'ملاحظة المراجعة',
};

const ACTIONS: Record<string, string> = {
  insert: 'أُنشئت',
  update: 'عُدِّلت',
  delete: 'حُذفت',
};

export type ChangeLine = { field: string; before: string; after: string };

function show(column: string, v: unknown, names: Map<string, string>): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'نعم' : 'لا';
  if (column === 'amount' || column === 'estimated_value') {
    return dirhams(String(v));
  }
  if (column === 'type') return TYPE_LABELS[v as OperationType] ?? String(v);
  if (column === 'source') {
    return SOURCE_LABELS[v as OperationSource] ?? String(v);
  }
  if (column.endsWith('photo_id')) return 'مرفق';
  if (column === 'debt_id') return 'مرتبط بقرض';
  if (column.endsWith('_id')) return names.get(String(v)) ?? 'محذوف';
  return String(v);
}

/** The changed fields of one history row, those people know. */
export function changeLines(
  changes: unknown,
  names: Map<string, string>,
): ChangeLine[] {
  if (!changes || typeof changes !== 'object') return [];
  return Object.entries(changes as Record<string, unknown>).flatMap(
    ([column, pair]) => {
      const label = FIELD_LABELS[column];
      if (!label || !Array.isArray(pair)) return [];
      return [
        {
          field: label,
          before: show(column, pair[0], names),
          after: show(column, pair[1], names),
        },
      ];
    },
  );
}

export const actionLabel = (action: string) => ACTIONS[action] ?? action;
